import { useState, useCallback } from 'react'

import { MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'

export { MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'

export type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES]

interface EmailChangeState {
  isEmailChangeModalOpen: boolean
  isConfirmModalOpen: boolean
  isErrorModalOpen: boolean
  newEmail: string | null
  errorType: ModalType | null
  confirmationType: ModalType | null
}

interface EmailChangeActions {
  openEmailChangeModal: () => void
  closeEmailChangeModal: () => void
  closeConfirmModal: () => void
  openErrorModal: (errorType: ModalType) => void
  closeErrorModal: () => void
  handleEmailChangeSuccess: (email: string) => void
}

type UseEmailChangeFlow = EmailChangeState & EmailChangeActions

export const useEmailChange = (): UseEmailChangeFlow => {
  const [state, setState] = useState<EmailChangeState>({
    isEmailChangeModalOpen: false,
    isConfirmModalOpen: false,
    isErrorModalOpen: false,
    newEmail: null,
    errorType: null,
    confirmationType: null,
  })

  const openEmailChangeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isEmailChangeModalOpen: true,
    }))
  }, [])

  const closeEmailChangeModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isEmailChangeModalOpen: false,
    }))
  }, [])

  const closeConfirmModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isConfirmModalOpen: false,
    }))
  }, [])

  const openErrorModal = useCallback((type: ModalType) => {
    setState(prev => ({
      ...prev,
      isEmailChangeModalOpen: false,
      isErrorModalOpen: true,
      errorType: type,
    }))
  }, [])

  const closeErrorModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isErrorModalOpen: false,
      errorType: null,
    }))
  }, [])

  const handleEmailChangeSuccess = useCallback((email: string) => {
    setState(prev => ({
      ...prev,
      isEmailChangeModalOpen: false,
      isConfirmModalOpen: true,
      newEmail: email,
      confirmationType: MODAL_TYPES.CHANGE_EMAIL,
    }))
  }, [])

  return {
    ...state,
    openEmailChangeModal,
    closeEmailChangeModal,
    closeConfirmModal,
    openErrorModal,
    closeErrorModal,
    handleEmailChangeSuccess,
  }
}
